import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { scrollToSection } from "@/components/Header";

const SERVICES = [
  {
    num: "01",
    title: "General Plumbing",
    items: ["Leaking taps", "Running / blocked toilets", "Blocked sinks", "Water pressure issues", "Leaking pipes & fittings", "Fixture repairs & replacements", "Kitchen & bathroom plumbing", "Installation of appliances"],
  },
  {
    num: "02",
    title: "Hot Water Service",
    items: ["Hot water system servicing", "Hot water repairs", "New system installation", "Hot water system replacement"],
  },
  {
    num: "03",
    title: "Gas",
    items: ["Gas repairs", "Gas appliance connections", "Gas leak investigation", "Gas safety checks"],
  },
  {
    num: "04",
    title: "CCTV Drain Camera",
    items: ["Camera inspection of underground drains", "Identify blockages, cracks & tree-root intrusion", "Pinpoint the location of drainage issues", "Clear recommendations for repairs or cleaning"],
  },
  {
    num: "05",
    title: "High-Pressure Water Jetter",
    items: ["Clear blocked drains & sewer lines", "Remove built-up grease, debris & sediment", "Cut through tree-root intrusion", "Clean pipe walls to prevent repeat blockages", "Renovation drainage preparation & clean-up"],
  },
  {
    num: "06",
    title: "Drainage",
    items: ["Blocked drain clearing", "Drain repairs & replacements", "Stormwater drainage solutions", "Drainage excavation & installation"],
  },
  {
    num: "07",
    title: "Water Filtration",
    items: ["Installation & replacement of water filters", "Whole-house water filtration systems"],
  },
  {
    num: "08",
    title: "Renovations",
    items: ["Kitchen renovation plumbing", "Bathroom renovation plumbing", "Laundry plumbing upgrades", "Fixture & appliance installation"],
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="border-b border-[#111111]/15">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
          <motion.h2
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="font-display uppercase tracking-wide leading-none text-5xl md:text-7xl"
          >
            What We<br />
            <span className="text-[#003BFF]">Do Best</span>
          </motion.h2>
          <motion.p
            variants={reveal}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="max-w-sm text-base text-[#555555] font-medium leading-relaxed"
          >
            From leaking taps to full drainage works — one licensed crew for
            every plumbing job, big or small.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 border-t border-l border-[#111111]/15">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.num}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              data-testid={`service-card-${s.num}`}
              onClick={() => scrollToSection("#contact")}
              className={`lg:col-span-6 group relative border-b border-r border-[#111111]/15 p-8 md:p-12 cursor-pointer hover:bg-white transition-colors duration-500 flex flex-col justify-between gap-10 min-h-[300px]`}
            >
              <div className="flex items-start justify-between">
                <span className="font-display text-sm tracking-[0.3em] text-[#003BFF]">{s.num}</span>
                <ArrowUpRight className="w-6 h-6 text-[#111111]/30 transition-all duration-300 group-hover:text-[#003BFF] group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
              <div>
                <h3 className="font-display uppercase tracking-wide text-2xl md:text-3xl leading-tight mb-5">
                  {s.title}
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-xl">
                  {s.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[#555555] font-medium leading-snug">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#003BFF]" strokeWidth={3} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <span className="absolute bottom-0 left-0 h-[3px] w-0 bg-[#003BFF] transition-[width] duration-500 group-hover:w-full" />
            </motion.article>
          ))}

          <motion.div
            custom={4}
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            data-testid="services-image-placeholder"
            className="lg:col-span-7 relative border-b border-r border-[#111111]/15 bg-white flex flex-col overflow-hidden"
          >
            <div className="px-8 md:px-10 py-5 border-b border-[#111111]/15 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-display uppercase tracking-wide text-xl md:text-2xl" data-testid="bathroom-caption">
                Bathroom Renovation — Vanity &amp; Tapware
              </h3>
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#003BFF]">
                Recent Project
              </span>
            </div>
            <img
              src="/assets/bathroom-vanity.webp"
              alt="Bathroom renovation with new vanity basin and brushed brass tapware by One Piece Maintenance"
              className="w-full h-auto block"
            />
          </motion.div>

          <motion.div
            custom={5}
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            data-testid="services-cta-cell"
            onClick={() => scrollToSection("#contact")}
            className="lg:col-span-5 group border-b border-r border-[#111111]/15 bg-[#003BFF] text-white p-8 md:p-12 cursor-pointer flex flex-col justify-between min-h-[280px] md:min-h-[340px] hover:bg-[#111111] transition-colors duration-500"
          >
            <span className="font-display text-sm tracking-[0.3em] opacity-70">09</span>
            <div>
              <h3 className="font-display uppercase tracking-wide text-2xl md:text-4xl leading-tight mb-4">
                Not Sure What You Need?
              </h3>
              <p className="text-sm md:text-base font-medium leading-relaxed opacity-80 mb-6">
                Call <a href="tel:+61452028256" data-testid="services-phone-link" className="underline underline-offset-4">0452 028 256</a> or send a quote request — we'll diagnose it properly.
              </p>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] border-b-2 border-white/50 pb-1 group-hover:border-white transition-colors duration-300">
                Get in touch <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
